import React, { useCallback, useMemo, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppTimeField } from '../../components/AppTimeField';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { getNotificationModuleCapabilities } from '../../config/notificationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../../services/notificationService';
import type {
  NotificationPreferenceInput,
  NotificationPreferences,
} from '../../types/notification';
import { notificationErrorMessage } from '../../utils/notificationErrors';
import { getNotificationTypeLabel } from '../../utils/notificationType';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'NotificationPreferences'
>;

type Draft = NotificationPreferenceInput & {
  notificationType: string;
};

function toDraft(item: NotificationPreferences): Draft {
  return {
    notificationType: item.notificationType,
    inAppEnabled: item.inAppEnabled,
    pushEnabled: item.pushEnabled,
    emailEnabled: item.emailEnabled,
    smsEnabled: item.smsEnabled,
    quietHoursEnabled: item.quietHoursEnabled,
    quietHoursStart: item.quietHoursStart,
    quietHoursEnd: item.quietHoursEnd,
    preferredLanguageCode: item.preferredLanguageCode || 'en',
  };
}

function ChannelSwitch({
  label,
  value,
  onValueChange,
  disabled,
  palette,
}: {
  label: string;
  value: boolean;
  onValueChange: (next: boolean) => void;
  disabled?: boolean;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.switchRow}>
      <Text style={[styles.switchLabel, { color: palette.textPrimary }]}>
        {label}
      </Text>
      <Switch
        accessibilityLabel={label}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      />
    </View>
  );
}

export function NotificationPreferencesScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getNotificationModuleCapabilities(auth.permissions);

  const [items, setItems] = useState<NotificationPreferences[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
      },
      typeChip: {
        borderColor: palette.border,
        backgroundColor: palette.surface,
      },
      typeChipSelected: {
        borderColor: palette.primary,
        backgroundColor: palette.primary,
      },
      typeChipText: { ...typography.caption, color: palette.textPrimary },
      typeChipTextSelected: { color: palette.onPrimary, fontWeight: '700' as const },
      error: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.sm,
      },
      success: {
        ...typography.caption,
        color: palette.success,
        marginBottom: spacing.sm,
      },
    }),
    [palette],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canViewPreferences) {
        setLoading(false);
        setRefreshing(false);
        setError('Notification preferences are not currently available.');
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const next = await getNotificationPreferences();
        setItems(next);
        setDrafts(next.map(toDraft));
        setSelectedType(current =>
          current && next.some(item => item.notificationType === current)
            ? current
            : next[0]?.notificationType ?? null,
        );
      } catch (caught) {
        setItems([]);
        setDrafts([]);
        setError(
          notificationErrorMessage(
            caught,
            'Notification preferences are not currently available.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canViewPreferences],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const selectedDraft = drafts.find(
    item => item.notificationType === selectedType,
  );

  const updateDraft = (patch: Partial<Draft>) => {
    if (!selectedType) return;
    setDrafts(current =>
      current.map(item =>
        item.notificationType === selectedType ? { ...item, ...patch } : item,
      ),
    );
    setMessage(null);
  };

  const save = async () => {
    if (!capabilities.canUpdatePreferences || !selectedDraft || saving) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await runOperation('update', () => updateNotificationPreferences(
        selectedDraft.notificationType,
        {
          inAppEnabled: selectedDraft.inAppEnabled,
          pushEnabled: selectedDraft.pushEnabled,
          emailEnabled: selectedDraft.emailEnabled,
          smsEnabled: selectedDraft.smsEnabled,
          quietHoursEnabled: selectedDraft.quietHoursEnabled,
          quietHoursStart: selectedDraft.quietHoursStart,
          quietHoursEnd: selectedDraft.quietHoursEnd,
          preferredLanguageCode: selectedDraft.preferredLanguageCode,
        },
      ));
      setItems(current =>
        current.map(item =>
          item.notificationType === saved.notificationType ? saved : item,
        ),
      );
      setDrafts(current =>
        current.map(item =>
          item.notificationType === saved.notificationType
            ? toDraft(saved)
            : item,
        ),
      );
      setMessage('Preferences saved.');
    } catch (caught) {
      setError(
        notificationErrorMessage(
          caught,
          'Unable to save notification preferences.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!capabilities.canViewPreferences) {
    return (
      <AppScreen webLayout="narrow" contentStyle={styles.center}>
        <ScreenHeader
          title="Notification preferences"
          onBack={navigation.goBack}
        />
        <EmptyState
          title="Preferences unavailable"
          message="Notification preferences are not currently available."
        />
      </AppScreen>
    );
  }

  if (loading && !items.length) {
    return (
      <AppScreen webLayout="narrow" contentStyle={styles.center}>
        <ScreenHeader
          title="Notification preferences"
          onBack={navigation.goBack}
        />
        <AppLoader message="Loading preferences" />
      </AppScreen>
    );
  }

  if (error && !items.length) {
    return (
      <AppScreen webLayout="narrow" contentStyle={styles.center}>
        <ScreenHeader
          title="Notification preferences"
          onBack={navigation.goBack}
        />
        <ErrorState message={error} onRetry={() => load()} />
      </AppScreen>
    );
  }

  return (
    <AppScreen webLayout="narrow"
      scroll
      keyboardSafe
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => load(true)}
          tintColor={palette.primary}
        />
      }
    >
      <ScreenHeader
        title="Notification preferences"
        onBack={navigation.goBack}
      />
      <View style={styles.body}>
      {items.length === 0 ? (
        <EmptyState
          title="No preferences"
          message="Notification preferences are not currently available."
        />
      ) : (
        <>
          <Text style={themed.section}>Notification type</Text>
          <View style={styles.typeList}>
            {items.map(item => {
              const selected = item.notificationType === selectedType;
              return (
                <Pressable
                  key={item.notificationType}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={getNotificationTypeLabel(
                    item.notificationType,
                  )}
                  onPress={() => setSelectedType(item.notificationType)}
                  style={[
                    styles.typeChip,
                    themed.typeChip,
                    selected && themed.typeChipSelected,
                  ]}
                >
                  <Text
                    style={[
                      themed.typeChipText,
                      selected && themed.typeChipTextSelected,
                    ]}
                  >
                    {getNotificationTypeLabel(item.notificationType)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {selectedDraft ? (
            <AppCard style={styles.card}>
              <Text style={themed.section}>
                {getNotificationTypeLabel(selectedDraft.notificationType)}
              </Text>
              <ChannelSwitch
                label="In-app notifications"
                value={selectedDraft.inAppEnabled}
                onValueChange={value => updateDraft({ inAppEnabled: value })}
                disabled={!capabilities.canUpdatePreferences}
                palette={palette}
              />
              <ChannelSwitch
                label="Email notifications"
                value={selectedDraft.emailEnabled}
                onValueChange={value => updateDraft({ emailEnabled: value })}
                disabled={!capabilities.canUpdatePreferences}
                palette={palette}
              />
              <ChannelSwitch
                label="SMS notifications"
                value={selectedDraft.smsEnabled}
                onValueChange={value => updateDraft({ smsEnabled: value })}
                disabled={!capabilities.canUpdatePreferences}
                palette={palette}
              />
              {/* Backend exposes pushEnabled on preferences; device push registration is not implemented. */}
              <ChannelSwitch
                label="Push notifications"
                value={selectedDraft.pushEnabled}
                onValueChange={value => updateDraft({ pushEnabled: value })}
                disabled={!capabilities.canUpdatePreferences}
                palette={palette}
              />
              <ChannelSwitch
                label="Quiet hours"
                value={selectedDraft.quietHoursEnabled}
                onValueChange={value =>
                  updateDraft({
                    quietHoursEnabled: value,
                    quietHoursStart: value
                      ? selectedDraft.quietHoursStart || '22:00'
                      : null,
                    quietHoursEnd: value
                      ? selectedDraft.quietHoursEnd || '07:00'
                      : null,
                  })
                }
                disabled={!capabilities.canUpdatePreferences}
                palette={palette}
              />
              {selectedDraft.quietHoursEnabled ? (
                <>
                  <AppTimeField
                    label="Quiet hours start"
                    value={selectedDraft.quietHoursStart || ''}
                    onChange={value =>
                      updateDraft({ quietHoursStart: value })
                    }
                  />
                  <AppTimeField
                    label="Quiet hours end"
                    value={selectedDraft.quietHoursEnd || ''}
                    onChange={value =>
                      updateDraft({ quietHoursEnd: value })
                    }
                  />
                </>
              ) : null}
              <AppInput
                label="Preferred language"
                value={selectedDraft.preferredLanguageCode}
                onChangeText={value =>
                  updateDraft({ preferredLanguageCode: value })
                }
                autoCapitalize="none"
              />
            </AppCard>
          ) : null}
        </>
      )}

      {error ? (
        <Text accessibilityRole="alert" style={themed.error}>
          {error}
        </Text>
      ) : null}
      {message ? (
        <Text accessibilityRole="alert" style={themed.success}>
          {message}
        </Text>
      ) : null}

      <View style={styles.actions}>
        {capabilities.canUpdatePreferences && selectedDraft ? (
          <AppButton title="Save preferences" onPress={save} loading={saving} />
        ) : null}
      </View>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  typeList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  typeChip: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  card: { gap: spacing.md, marginBottom: spacing.md },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  switchLabel: { ...typography.body, flex: 1 },
  actions: { gap: spacing.sm, marginBottom: spacing.xl },
});
